import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Minus, Dumbbell } from 'lucide-react';
import { supabase } from '@/lib/customSupabaseClient';

const SEVERITY_COLORS = {
  high: 'destructive',
  moderate: 'warning',
  low: 'secondary',
  none: 'outline',
};

const METRIC_LABELS = {
  vertical_ratio: { title: 'Vertical Ratio', unit: '%', averageLabel: 'Average VR' },
  ground_contact_time: { title: 'Ground Contact Time', unit: 'ms', averageLabel: 'Average GCT' },
  cadence: { title: 'Cadence', unit: 'spm', averageLabel: 'Average' },
  stride_length: { title: 'Stride Length', unit: 'm', averageLabel: 'Average' },
  stance_balance: { title: 'Stance Balance', unit: '%L', averageLabel: 'Average' },
};

function DriftIcon({ direction }) {
  if (direction === 'increasing') return <TrendingUp className="h-4 w-4 text-orange-400" />;
  if (direction === 'decreasing') return <TrendingDown className="h-4 w-4 text-green-400" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

function formatValue(value, unit) {
  if (value == null) return '—';
  if (unit === '%') return `${value}%`;
  if (unit === 'ms') return `${value}ms`;
  if (unit === 'spm') return `${value}`;
  if (unit === 'm') return `${value}m`;
  return `${value}${unit}`;
}

function SingleInsightCard({ insight }) {
  const content = insight.content || {};
  const severity = content.severity || 'none';
  const muscleSentences = content.muscle_sentences || [];
  const metricInfo = METRIC_LABELS[insight.metric_key] || {
    title: insight.metric_key,
    unit: '',
    averageLabel: 'Average',
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Running Dynamics — {metricInfo.title}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={SEVERITY_COLORS[severity] || 'outline'}>
              {insight.status}
            </Badge>
            <Badge variant="outline">v{insight.analyzer_version}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {content.definition && (
          <p className="text-sm text-muted-foreground italic">{content.definition}</p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              {metricInfo.averageLabel}
            </p>
            <p className="text-2xl font-bold">
              {formatValue(insight.average_value, metricInfo.unit)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">First segment</p>
            <p className="text-lg font-semibold">
              {formatValue(insight.first_segment_value, metricInfo.unit)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Last segment</p>
            <p className="text-lg font-semibold">
              {formatValue(insight.last_segment_value, metricInfo.unit)}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Drift</p>
            <div className="flex items-center gap-2">
              <DriftIcon direction={insight.drift_direction} />
              <p className="text-lg font-semibold">
                {insight.drift_percent != null
                  ? `${insight.drift_percent > 0 ? '+' : ''}${insight.drift_percent}%`
                  : '—'}
              </p>
            </div>
          </div>
        </div>

        {(content.narrative_title || content.narrative_description) && (
          <div className="rounded-lg border p-4 space-y-1">
            <p className="text-sm font-medium">{content.narrative_title}</p>
            <p className="text-sm text-muted-foreground">{content.narrative_description}</p>
          </div>
        )}

        {muscleSentences.length > 0 && (
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Dumbbell className="h-4 w-4" />
              <p className="text-sm font-medium">
                Key muscles governing {metricInfo.title.toLowerCase()}
              </p>
            </div>
            <div className="space-y-2">
              {muscleSentences.map((muscle, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 rounded border px-3 py-2"
                >
                  <Badge variant="outline" className="text-xs mt-0.5 flex-shrink-0">
                    {muscle.role}
                  </Badge>
                  <p className="text-sm text-muted-foreground">{muscle.sentence}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span>Samples: {insight.clean_sample_count}/{insight.sample_count}</span>
          {content.peripheral_fatigue_label && (
            <span>Fatigue signal: {content.peripheral_fatigue_label}</span>
          )}
          <span>Narrative: {insight.narrative_key}</span>
          {insight.analyzed_at && (
            <span>
              Analyzed: {new Date(insight.analyzed_at).toLocaleString()}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const RunningDynamicsInsightCard = ({ userId, garminActivityId, refreshKey = 0 }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !garminActivityId) return;

    const fetchInsights = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('garmin_running_dynamics_insights')
        .select('*')
        .eq('user_id', userId)
        .eq('garmin_activity_id', garminActivityId)
        .order('metric_key');

      if (!error && data) setInsights(data);
      setLoading(false);
    };

    fetchInsights();
  }, [userId, garminActivityId, refreshKey]);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Running Dynamics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (insights.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Running Dynamics</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No dynamics insights computed for this activity. Re-analyze to generate.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {insights.map((insight) => (
        <SingleInsightCard key={insight.id} insight={insight} />
      ))}
    </div>
  );
};

export default RunningDynamicsInsightCard;
